import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  getToolLabel,
  formatParallelToolsLabel,
  formatToolArgsLabel,
} from '../../streaming/toolLabels';
import ActivityGlyph from './ActivityGlyph';
import useElapsedTime from '../../hooks/useElapsedTime';

/**
 * StreamingToolLoader — visible UI cue while an agent is executing one or
 * more tools. Mirrors the `dotPulse` pattern from SimpleTypingIndicator
 * (ChatMessages.jsx:30-49) for visual consistency.
 *
 * Two complementary signals feed this loader:
 * - `tools`: from `stream-tool-call-start` — gives the curated icon + label.
 *   Often ABSENT inside an N8N Agent V2 loop (the fork's tool-call-start never
 *   reaches the callback — see Chat.jsx onDelta).
 * - `statusText`: the live mid-tool progress from the `agent_status_updated`
 *   side-channel (e.g. "Buscando…" → "Sintetizando respuesta…"). This is the
 *   reliable wait-state signal and works with or without `tools`.
 *
 * Render modes:
 * - Single tool: icon + curated label + sub-label (statusText wins over args).
 * - Parallel tools: combined label "A, B y C", no sub-label.
 * - No tools, only statusText: status text + dots (no icon) — the Agent-V2 path.
 */
function StreamingToolLoader({ tools, statusText, visible = true }) {
  const theme = useTheme();
  const { t } = useTranslation();

  // ⚠️ ANTES de cualquier early return: este componente devuelve `null` cuando no hay
  // nada que mostrar, así que un hook colocado después cambiaría el orden de hooks en
  // el render en que pasa de oculto a visible — y eso revienta en producción aunque los
  // tests, que renderizan un caso fijo cada uno, salgan verdes.
  //
  // Y el reloj mide el TURNO, no cada aparición: el consumidor deja este componente
  // montado toda la ronda y lo apaga con `visible`, en vez de desmontarlo (#923). Si
  // se volviera a montar condicionalmente, el contador arrancaría de cero en cada
  // ronda tool→texto→tool y subestimaría la espera — el defecto que arregló esto.
  const { label: elapsedLabel } = useElapsedTime(true);

  const hasTools = Array.isArray(tools) && tools.length > 0;
  const status = statusText || null;

  // Nothing to show only when there is neither a tool nor a live status.
  if (!visible || (!hasTools && !status)) return null;

  const primary = hasTools ? tools[0] : null;
  // #1003 F7: `tool.label` viene resuelto por el backend (`toolCatalogService`) en el evento y en
  // el snapshot. Sin él se pinta un genérico — nunca el identificador del nodo, que es lo que
  // este loader enseñaba hasta F7 (`Ejecutando T__Consultar_Ficheros`).
  const toolMeta = hasTools ? getToolLabel(primary?.name, t, primary?.label) : null;
  const icon = toolMeta?.icon ?? null;

  // Primary label: the curated tool label when tool-call-start arrived,
  // otherwise the live status text.
  const label = hasTools
    ? (tools.length === 1 ? toolMeta.label : formatParallelToolsLabel(tools, t))
    : status;

  // Sub-label (single tool only): the live status wins — it is the freshest,
  // sub-workflow-emitted progress — falling back to the arg-derived hint.
  const subLabel = hasTools && tools.length === 1
    ? (status || formatToolArgsLabel(primary?.name, primary?.args))
    : null;

  const glyphColor = theme?.chat?.typing?.dotColor || theme.palette.text.disabled;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 1,
        my: 0.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      {/* Latido, igual que en el indicador sin streaming: sustituye a los tres puntos */}
      <ActivityGlyph size={14} color={glyphColor} />

      {icon && (
        <Box component="span" aria-hidden="true" sx={{ fontSize: '1.1em', flexShrink: 0 }}>{icon}</Box>
      )}
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontWeight: 500,
          fontSize: '0.875rem',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {subLabel && (
          <Typography
            component="span"
            variant="body2"
            sx={{
              ml: 0.75,
              color: 'text.secondary',
              fontStyle: 'italic',
              fontSize: 'inherit',
            }}
          >
            · {subLabel}
          </Typography>
        )}
      </Typography>
      {elapsedLabel && (
        <Typography
          component="span"
          variant="caption"
          // Fuera del anuncio: este contenedor es `aria-live`, y un contador que cambia
          // cada segundo haría que el lector de pantalla lo recitara sin parar — en el
          // peor turno medido (22 min) serían ~1.300 anuncios. El tiempo es señal
          // visual; lo que se anuncia es el texto del estado.
          aria-hidden="true"
          sx={{
            color: 'text.disabled',
            flexShrink: 0,
            // Cifras de ancho fijo: si no, el número baila al pasar de 9s a 10s.
            fontVariantNumeric: 'tabular-nums',
            fontSize: '0.75rem',
          }}
        >
          {elapsedLabel}
        </Typography>
      )}
    </Box>
  );
}

StreamingToolLoader.propTypes = {
  tools: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    args: PropTypes.object,
    id: PropTypes.string,
    // Etiqueta en castellano resuelta por el backend (#1003 F7). `null` cuando el catálogo no
    // pudo resolverla ⇒ el loader cae a un genérico.
    label: PropTypes.string,
  })),
  // Live mid-tool status from the agent_status_updated side-channel.
  statusText: PropTypes.string,
  // Apaga el render SIN desmontar, para que el cronómetro siga midiendo el turno
  // completo a través de las rondas tool→texto→tool (#923).
  visible: PropTypes.bool,
};

export default React.memo(StreamingToolLoader);
